import { Color as DTColor, Spacing } from '@persona-id/design-tokens';
import { BriefcaseIcon } from '@persona-id/design-tokens/icons';
import Color from 'color';
import { useCallback } from 'react';
import { UseFormSetValue } from 'react-hook-form';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom-v5-compat';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import GraphVertexIcon from 'dashboard/components/GraphVertexIcon';
import NavBack from 'dashboard/components/NavBack';
import Permissioned from 'dashboard/components/Permissioned';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import NodeFunctionsConfigurationTab from 'dashboard/forms/FormGraphNodeConfiguration/NodeFunctionsConfigurationTab/NodeFunctionsConfigurationTab';
import NodeKeyTab from 'dashboard/forms/FormGraphNodeConfiguration/NodeKeyTab';
import Form from 'dashboard/layouts/FormLayout/Form';
import { Container, Header, Section as LayoutSection } from 'dashboard/layouts/IndexViewLayoutV2';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { AccountType } from 'dashboard/models/account-type';
import { DatumValueType } from 'dashboard/models/graph';
import { GraphProperty, NodeRenderConfig } from 'dashboard/models/graph/node-render-config';
import {
  ServerNodeFunctionsConfig,
  ServerNodeRenderConfig,
  ServerPresetSummary,
} from 'dashboard/models/graph-backend';
import { GraphDatumType } from 'dashboard/models/graph-datum-type';
import { showMessage } from 'dashboard/redux/actions/messages';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import usePermission from 'lib/hooks/usePermission';
import { Permission } from 'lib/permissions';
import { useFormSecurely } from 'lib/security/reactHookForm';

import EmailSimilarityConfigurationTab from './EmailSimilarityConfigurationTab';
import HoverCardPropertiesTab from './HoverCardPropertiesTab';

const CardContainer = styled(Card)`
  min-height: 400px;
  display: flex;
  flex-direction: column;
  padding: ${Spacing[5]};
`;

const CardBody = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: ${DTColor.BackgroundSecondary};
  border-radius: ${BORDER_RADIUSES.rounded};
  text-align: center;
`;

const IconWrapper = styled.div`
  margin-bottom: ${Spacing[2]};
`;

const HeaderPrependContainer = styled('div')`
  display: flex;
  align-items: center;
  margin-right: ${Spacing[2]};
`;

interface FormGraphNodeConfigurationDispatchProps {
  showMessage: (...args: Parameters<typeof showMessage>) => void;
}

export interface GraphNodeConfigFormData {
  nodeRenderConfig: ServerNodeRenderConfig;
  nodeFunctionsConfig: ServerNodeFunctionsConfig;
  graphDatumType: GraphDatumType;
}

interface FormGraphNodeConfigurationProps {
  accountTypes: AccountType[];
  nodeRenderConfig?: ServerNodeRenderConfig;
  graphDatumType?: GraphDatumType;
  nodeFunctionsConfig?: ServerNodeFunctionsConfig;
  presetSummaries: ServerPresetSummary[];
  defaultGraphProperties?: GraphProperty[];
  handleSubmit: (data: GraphNodeConfigFormData) => void;
  isSubmitting: boolean;
  isCustomNode: boolean;
}

const FormGraphNodeConfiguration = ({
  defaultGraphProperties,
  nodeRenderConfig,
  graphDatumType,
  nodeFunctionsConfig,
  accountTypes,
  presetSummaries,
  handleSubmit: onSubmit,
  isSubmitting,
  showMessage,
  isCustomNode,
}: FormGraphNodeConfigurationProps & FormGraphNodeConfigurationDispatchProps) => {
  const { pathname } = useLocation();
  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isDirty },
    reset,
  } = useFormSecurely<GraphNodeConfigFormData>({
    defaultValues: {
      nodeRenderConfig,
      nodeFunctionsConfig: {
        ...nodeFunctionsConfig,
        sources: nodeFunctionsConfig?.sources.map((source) => ({
          ...source,
          'function-mappings': source['function-mappings'].map((fm) => ({
            ...fm,
            'param-mappings': fm['param-mappings'].map((pm) => {
              if (pm['function-chain-input-token']) {
                const fnIdx = nodeFunctionsConfig.functions.findIndex(
                  (x) => x.token === pm['function-chain-input-token'],
                );
                const prefix = `function_${fnIdx + 1}_${nodeFunctionsConfig.functions[fnIdx].preset}`;
                return {
                  ...pm,
                  path: pm.path ? prefix + '.' + pm.path : prefix,
                };
              } else {
                return {
                  ...pm,
                  path: pm.path ? 'source.' + pm.path : '',
                };
              }
            }),
          })),
          'node-key': [...source['node-key']]
            .sort(
              (a, b) =>
                nodeFunctionsConfig['node-key-schema'].findIndex((x) => x.key === a.key) -
                nodeFunctionsConfig['node-key-schema'].findIndex((x) => x.key === b.key),
            )
            .map((nk) => {
              const fnIdx = nodeFunctionsConfig.functions.findIndex(
                (x) => x.token === nk['function-token'],
              );
              const prefix = `function_${fnIdx + 1}_${nodeFunctionsConfig.functions[fnIdx].preset}`;
              return {
                ...nk,
                path: nk.path ? prefix + '.' + nk.path : prefix,
              };
            }),
        })),
      },
    },
  });

  // React-hook-form does not support auto checking if nested objects are dirty so need to manually set
  const setValueAndUpdateIsDirty: UseFormSetValue<GraphNodeConfigFormData> = useCallback(
    (name, value) => setValue(name, value, { shouldDirty: true }),
    [setValue],
  );

  const nodeFunctionsConfigFormValue = watch('nodeFunctionsConfig');

  const isNodeKeyValid = () => {
    // For 'Account' node config, node functions are undefined.
    if (!nodeFunctionsConfig) {
      return true;
    }

    const nodeKeySchemaKeys = nodeFunctionsConfigFormValue['node-key-schema'].map(
      (nodeKey) => nodeKey.key,
    );

    const nodeKeySchemaNamesNotEmpty = nodeKeySchemaKeys.every((key) => !!key);

    const hasDistinctKeyNames = new Set(nodeKeySchemaKeys).size === nodeKeySchemaKeys.length;

    // Sanity check, should never be false.
    const nodeKeySchemaMatchesNodeKey = nodeFunctionsConfigFormValue.sources.every((source) => {
      return (
        nodeKeySchemaKeys.length === source['node-key'].length &&
        source['node-key'].every((nodeKey, index) => nodeKey.key === nodeKeySchemaKeys[index])
      );
    });

    const nodeKeyFunctionTokensNotEmpty = nodeFunctionsConfigFormValue.sources.every((source) =>
      source['node-key'].every((nodeKey) => !!nodeKey['function-token']),
    );

    return (
      nodeKeySchemaNamesNotEmpty &&
      hasDistinctKeyNames &&
      nodeKeySchemaMatchesNodeKey &&
      nodeKeyFunctionTokensNotEmpty
    );
  };

  // This bool checks if all 'required' param mappinngs are mapped.
  const allRequiredFieldsFilled = nodeFunctionsConfig
    ? nodeFunctionsConfigFormValue.sources.every((source) => {
        return source['function-mappings'].every((fm) => {
          // Get the preset summary for the function
          const presetSummary = presetSummaries.find(
            (ps) =>
              ps.preset ===
              nodeFunctionsConfigFormValue.functions.find((f) => f.token === fm.token)?.preset,
          );
          return (
            !!presetSummary &&
            fm['param-mappings'].every((pm) => {
              // Get the specific FieldSchema for the param mapping
              const paramFieldSchema = presetSummary['param-schema'].find(
                (fs) => fs.key === pm.destination,
              );

              // All param mappings must either be not required or have a path/direct value
              return (
                !!paramFieldSchema &&
                (!paramFieldSchema.config.required || !!pm.path || !!pm['direct-value'])
              );
            })
          );
        });
      })
    : true;

  const hasGraphWritePerms = usePermission(Permission.GraphWrite);

  const isNodeFunctionsConfigWriteEnabled = useFeatureFlag(
    'dashboard:graph-config-node-functions-tab-enable-write',
  );

  const isCustomNonSimilarityNode = isCustomNode && !nodeFunctionsConfig?.disabled;

  const isHoverCardTabEnabled =
    nodeRenderConfig &&
    ((defaultGraphProperties && defaultGraphProperties.length > 0) || isCustomNonSimilarityNode);

  const showCriteriaTab =
    useFeatureFlag('dashboard:graph-email-similarity') &&
    graphDatumType?.attributes['datum-type'] === DatumValueType.EmailSimilarity;

  const showNodeKeyTab = useFeatureFlag('dashboard:graph-multiple-node-functions');

  const isModifyNodeFunctionsConfigDisabled =
    !isNodeFunctionsConfigWriteEnabled || nodeFunctionsConfig?.disabled || !hasGraphWritePerms;

  const isSaveButtonDisabled =
    !isDirty ||
    isSubmitting ||
    ((pathname.split('/').includes('functions') || pathname.split('/').includes('node-key')) &&
      isModifyNodeFunctionsConfigDisabled);

  const onSubmitCheckErrors = (data: GraphNodeConfigFormData) => {
    if (!allRequiredFieldsFilled) {
      showMessage('Required function params must be mapped', MessageType.Failure);
    } else if (!isNodeKeyValid()) {
      showMessage('Node key config is invalid', MessageType.Failure);
    } else {
      onSubmit(data);
      // Reset the form dirty state after submitting
      reset(data);
    }
  };

  return (
    <Form id='graph-node-configuration' onSubmit={handleSubmit(onSubmitCheckErrors)}>
      <Header
        prepend={
          <HeaderPrependContainer>
            <NavBack to='/dashboard/graph-configuration' />
            {nodeRenderConfig && (
              <GraphVertexIcon
                version='v2'
                datumTypeToken={nodeRenderConfig['datum-type-token']}
                nodeRenderConfig={
                  {
                    color: Color(nodeRenderConfig.color),
                    icon: nodeRenderConfig.icon,
                    displayName: nodeRenderConfig['display-name'],
                    placeHolder: nodeRenderConfig['search-placeholder'],
                  } as NodeRenderConfig
                }
              />
            )}
          </HeaderPrependContainer>
        }
        title={nodeRenderConfig ? nodeRenderConfig['display-name'] : 'Graph Configuration'}
      >
        <Permissioned permission={Permission.GraphWrite}>
          <Button
            type='submit'
            width='auto'
            data-test='graph-node-configuration__submit'
            disabled={isSaveButtonDisabled}
          >
            Save
          </Button>
        </Permissioned>
      </Header>
      <Container>
        <LayoutSection>
          <RoutedTabbedSection
            tabs={[
              {
                name: 'Functions',
                path: '/functions',
                disabled: !nodeFunctionsConfig,
                contents: nodeFunctionsConfig ? (
                  <Permissioned
                    permission={Permission.AccountTypeView}
                    fallback={
                      <CardContainer>
                        <Card.Title>Functions</Card.Title>
                        <CardBody>
                          <IconWrapper>
                            <BriefcaseIcon size={48} />
                          </IconWrapper>
                          <div>
                            User permission required to view Node Function details. Please contact
                            your account admin if you&#39;d like to view Node Functions.
                          </div>
                        </CardBody>
                      </CardContainer>
                    }
                  >
                    <NodeFunctionsConfigurationTab
                      watch={watch}
                      control={control}
                      setValue={setValueAndUpdateIsDirty}
                      presetSummaries={presetSummaries}
                      accountTypes={accountTypes}
                      disabled={isModifyNodeFunctionsConfigDisabled}
                      name={nodeRenderConfig ? nodeRenderConfig['display-name'] : ''}
                    />
                  </Permissioned>
                ) : (
                  <></>
                ),
              },
              showNodeKeyTab && {
                name: 'Node Key',
                path: '/node-key',
                disabled: !nodeFunctionsConfig,
                contents: nodeFunctionsConfig ? (
                  <NodeKeyTab
                    control={control}
                    watch={watch}
                    setValue={setValueAndUpdateIsDirty}
                    accountTypes={accountTypes}
                    disabled={isModifyNodeFunctionsConfigDisabled}
                    presetSummaries={presetSummaries}
                  />
                ) : (
                  <></>
                ),
              },
              showCriteriaTab && {
                name: 'Criteria Configuration',
                path: '/criteria',
                contents: (
                  <EmailSimilarityConfigurationTab control={control} datumType={graphDatumType} />
                ),
              },
              {
                name: 'Hover Card',
                path: '/hover-card',
                disabled: !isHoverCardTabEnabled,
                contents: isHoverCardTabEnabled ? (
                  <HoverCardPropertiesTab
                    control={control}
                    watch={watch}
                    nodeRenderConfig={nodeRenderConfig}
                    defaultGraphProperties={defaultGraphProperties || []}
                    nodeKeySchema={nodeFunctionsConfigFormValue['node-key-schema']}
                    isCustomNode={isCustomNode}
                  />
                ) : (
                  <></>
                ),
              },
            ]}
          />
        </LayoutSection>
      </Container>
    </Form>
  );
};

const mapDispatchToProps = {
  showMessage,
};

export default connect(null, mapDispatchToProps)(FormGraphNodeConfiguration);
